// Fires an immediate local notification whenever a rental crosses into overdue
// during a polling cycle. Works alongside scheduleOverdueNotifications (which
// handles future scheduled alerts) — this one covers the reactive "just became
// overdue" case that polling detects after the fact.

import { useRef, useEffect } from 'react'
import { isNativePlatform } from './platform'

interface Rental {
  id: string
  plannedReturnAt: string
  bike: { code: string }
}

let notifIdCounter = 900_000_000 // keep well away from scheduleOverdueNotifications IDs

export function useOverdueAlerts(rentals: Rental[]) {
  const prevOverdueIds = useRef<Set<string>>(new Set())
  // Skip on the very first render — don't alert for rentals already overdue when app opens
  const isFirstRun = useRef(true)

  useEffect(() => {
    if (!rentals.length) return

    const now = Date.now()
    const currentOverdueIds = new Set(
      rentals
        .filter((r) => r.plannedReturnAt && new Date(r.plannedReturnAt).getTime() < now)
        .map((r) => r.id),
    )

    if (isFirstRun.current) {
      // Silently initialise so the first real overdue after opening fires correctly
      prevOverdueIds.current = currentOverdueIds
      isFirstRun.current = false
      return
    }

    const newlyOverdue = rentals.filter(
      (r) => currentOverdueIds.has(r.id) && !prevOverdueIds.current.has(r.id),
    )

    prevOverdueIds.current = currentOverdueIds

    if (newlyOverdue.length === 0) return

    if (!isNativePlatform()) return

    import('@capacitor/local-notifications')
      .then(({ LocalNotifications }) => {
        const notifications = newlyOverdue.map((r) => ({
          id: notifIdCounter++,
          title: '⏰ Просрочка аренды',
          body: `${(r as any).lines?.length > 0 ? (r as any).lines.map((l: any) => `#${l.bike.code}`).join(', ') : `#${r.bike?.code ?? '?'}`} — время истекло, нужно закрыть аренду`,
          schedule: { at: new Date(Date.now() + 200) },
          channelId: 'overdue',
          actionTypeId: '',
          extra: { rentalId: r.id },
        }))
        return LocalNotifications.schedule({ notifications })
      })
      .catch(() => {})
  }, [rentals])
}
